"use client";

import {
  createTranslator,
  type NamespaceKeys,
  type NestedKeyOf,
} from "next-intl";
import { useContext, useMemo } from "react";
import {
  type CreatorMessages,
  CreatorMessagesContext,
} from "./CreatorMessagesContext";
import { type CreatorLocale, DEFAULT_CREATOR_LOCALE } from "./locales";
import en from "./messages/en";

/**
 * `useTranslations` for a creator component that also renders on an internal
 * page (the staff inbox and updates, the program page, the `/prototypes/one`
 * rack).
 *
 * Inside `/one` and `/join` it translates exactly as `useTranslations` would,
 * from the locale and catalogue the layout negotiated. Anywhere else there is
 * no provider, by design, and it translates from the English catalogue
 * instead of throwing. Keys are checked against English the same way.
 *
 * A component that only ever renders on `/one` or `/join` can keep using
 * next-intl's `useTranslations`; `tests/creatorInternalPages.test.ts` fails if
 * one that internal pages reach does.
 */
export function useCreatorTranslations<
  Namespace extends NamespaceKeys<
    CreatorMessages,
    NestedKeyOf<CreatorMessages>
  >,
>(namespace: Namespace) {
  const context = useContext(CreatorMessagesContext);
  const locale = context?.locale ?? DEFAULT_CREATOR_LOCALE;
  const messages = context?.messages ?? en;
  return useMemo(
    () => createTranslator({ locale, messages, namespace }),
    [locale, messages, namespace],
  );
}

/** The creator locale where there is one, English on internal pages. */
export function useCreatorLocale(): CreatorLocale {
  return useContext(CreatorMessagesContext)?.locale ?? DEFAULT_CREATOR_LOCALE;
}
